import { User } from "../users/User";
import { NumberIdentifiable } from "../common/Identifiable";
import {ProjectShort, ProjectRelatable, ProjectAreaName} from "./Project";
import {Identifier} from "../common/Identifier";
import {isPrivileged} from "../../config/Privileges";
import {Module} from "../common/Module";
import {SoftDeletable} from "../common/SoftDeletable";
import {ProjectLogTag} from "./ProjectLogTag";
import {ProjectLogCategory} from "./ProjectLogCategory";
import {MarkedRead} from "./MarkedRead";
import {Nullable} from "../common/Nullable";
import {ProjectStageShort} from "./ProjectStage";
import {ProjectStagePhaseShort} from "./ProjectStagePhase";
import {FileAttachmentsProp} from "../common/Attachments";

export const ProjectLogModule: Module = {
    identifier: "projects_log",
    isPrivileged: (authUser, privilege) => isPrivileged(
        authUser,
        ProjectAreaName,
        ProjectLogModule.identifier,
        privilege
    ),

};

export interface ProjectLog extends NumberIdentifiable, ProjectRelatable, SoftDeletable {
    entries: IProjectLogEntry[]
}

/**
 * an entry in a project log contains the following data
 */
export interface IProjectLogEntry extends ProjectLogEntryShort, SoftDeletable, FileAttachmentsProp {
    /** meta data project */
    project: ProjectShort
    /** meta data stage */
    projectStage: Nullable< ProjectStageShort >
    /** meta data phase */
    projectStagePhase: Nullable< ProjectStagePhaseShort >
    /** is > 0, when entry is an answer to another entry. else is 0 */
    answeredLogEntryId: Identifier<number>
    /** important entries will have raised awareness functionalities */
    isImportant: boolean
    /** entry creation time */
    timeOfEntry: string
    /** list of tags associated with this entry, filterable */
    tags: ProjectLogTag[]
    /** has the currently logged in user read this entry? */
    currentUserReadStatus: Nullable< MarkedRead >
}

export interface ProjectLogEntryId {
    /** unique identifier of the log entry */
    projectLogEntryId: Identifier<number>
}

export interface ProjectLogEntryProp {
    projectLogEntry: IProjectLogEntry
}

export interface ProjectLogId {
    projectLogId: Identifier<number>
}

export interface ProjectLogEntryShort extends ProjectLogId, ProjectLogEntryId {
    /** headline of the log entry */
    headline: string
    /** message of the log entry */
    message: string
    /** the author of the log entry */
    createdBy: User
}

/**
 * ProjectLogEntryRequest
 *
 * @property projectLogId unique id of the project log to post the entry in
 * @property categoryId unique id of the category to categorize the entry under
 * @property headline the headline of the entry
 * @property headline headline of the entry, *required field*, thus can't be empty and can't be null
 * @property message the entry itself, required field, thus can't be empty and can't be null
 * @property tags list of tags the entry has
 * @property isImportant the entry is an important one or not. a user can subscribe to only important messages if he wants.
 * @property projectLogEntryId is 0 on new entries. will contain the id of the entry on update
 * @property answeredLogEntryId is 0, when it is a new entry thread. will contain the id of the thread starting entry, when it is an answer
 * @property mentions the user can raise attention from specific users by naming them, this results in a notification for the mentioned user
 * @property projectId when projectLogId is 0, projectId, projectStageId and projectPhaseId will be used to fetch the log id
 * @property projectStageId see projectId
 * @property projectStagePhaseId see projectId
 */
export interface ProjectLogEntryRequest {
    projectLogId: number
    headline: string
    message: string
    tags: ProjectLogTag[]
    isImportant: boolean
    projectLogEntryId: number
    answeredLogEntryId: number
    mentions: ProjectLogEntryMentionRequest[]
    projectId: number
    projectStageId: number
    projectStagePhaseId: number
}

/**
 * creates a ProjectLogEntryRequest object to be used with a new form or when param set,
 * will create entry log request for update form
 *
 * @param { IProjectLogEntry | null } entry when set will create a new request object from it
 */
export function buildProjectLogEntryReq(
    entry: Nullable< IProjectLogEntry > = null ,
    isFilesOnly: boolean = false
): ProjectLogEntryRequest {

    if( entry === null ) {
        return {
            projectLogEntryId: 0,
            answeredLogEntryId: 0,
            projectLogId: 0,
            message: isFilesOnly ? "Dateien hinzugefügt" : "",
            headline: isFilesOnly ? 'Dateien hinzugefügt' :'',
            isImportant: false,
            tags: [],
            mentions: [],
            projectId: 0,
            projectStagePhaseId: 0,
            projectStageId: 0
        };
    } else {

        return {
            projectLogEntryId: entry.projectLogEntryId.rawValue,
            answeredLogEntryId: entry.answeredLogEntryId.rawValue,
            projectLogId: entry.projectLogId.rawValue,
            message: entry.message,
            headline: entry.headline,
            isImportant: entry.isImportant,
            tags: entry.tags,
            mentions: [],
            projectId: entry.project.projectId.rawValue,
            projectStagePhaseId: entry.projectStagePhase?.phaseId.rawValue ?? 0,
            projectStageId: entry.projectStage?.projectStageId.rawValue ?? 0,
        };
    }

}

/**
 * during creation of an project log entry, a user has the possibility of mentioning other users
 * these users will receive a notification about this entry.
 * This can raise awareness from people who should read this entry
 */
export interface ProjectLogEntryMention extends ProjectLogEntryId, SoftDeletable {
    mentioned: User
}


export interface ProjectLogEntryMentionRequest {
    mentioned: number
}

export interface ProjectLogEntryRequestProp {
    projectLogEntryReq: ProjectLogEntryRequest
}